// Firebase Storage 測試工具
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';

export interface StorageTestResult {
  testName: string;
  passed: boolean;
  message: string;
  details?: any;
}

export class FirebaseStorageTester {
  /**
   * 測試 1: 檢查認證狀態
   */
  async testAuth(): Promise<StorageTestResult> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        return {
          testName: '認證狀態檢查',
          passed: false,
          message: '用戶未登入',
          details: { user: null }
        };
      }
      
      return {
        testName: '認證狀態檢查',
        passed: true,
        message: `用戶已登入: ${user.uid}`,
        details: { uid: user.uid, email: user.email }
      };
    } catch (error: any) {
      return {
        testName: '認證狀態檢查',
        passed: false,
        message: `檢查失敗: ${error.message}`,
        details: { error }
      };
    }
  }

  /**
   * 測試 2: 檢查 Storage 規則
   */
  async testStorageRules(userId: string): Promise<StorageTestResult> {
    try {
      // 嘗試列出用戶的文件夾（這會觸發規則檢查）
      const testPath = `policies/${userId}`;
      const testRef = ref(storage, testPath);
      
      try {
        await listAll(testRef);
        return {
          testName: 'Storage 規則檢查',
          passed: true,
          message: '規則檢查通過（可以列出文件）',
          details: { path: testPath }
        };
      } catch (error: any) {
        if (error.code === 'storage/unauthorized') {
          return {
            testName: 'Storage 規則檢查',
            passed: false,
            message: '規則檢查失敗：沒有權限',
            details: { error: error.message, code: error.code }
          };
        }
        // 其他錯誤可能是文件夾不存在，這是正常的
        return {
          testName: 'Storage 規則檢查',
          passed: true,
          message: '規則檢查通過（文件夾不存在是正常的）',
          details: { path: testPath, error: error.message }
        };
      }
    } catch (error: any) {
      return {
        testName: 'Storage 規則檢查',
        passed: false,
        message: `檢查失敗: ${error.message}`,
        details: { error }
      };
    }
  }

  /**
   * 測試 3: 檢查文件路徑格式
   */
  testFilePathFormat(filePath: string): StorageTestResult {
    try {
      // 檢查路徑長度
      if (filePath.length > 1024) {
        return {
          testName: '文件路徑格式檢查',
          passed: false,
          message: `路徑過長: ${filePath.length} 字符（限制 1024）`,
          details: { path: filePath, length: filePath.length }
        };
      }

      // 檢查路徑格式
      if (!filePath.startsWith('policies/')) {
        return {
          testName: '文件路徑格式檢查',
          passed: false,
          message: '路徑必須以 policies/ 開頭',
          details: { path: filePath }
        };
      }

      // 檢查是否有特殊字符
      const invalidChars = /[#\[\]?*]/;
      if (invalidChars.test(filePath)) {
        return {
          testName: '文件路徑格式檢查',
          passed: false,
          message: '路徑包含無效字符',
          details: { path: filePath }
        };
      }

      return {
        testName: '文件路徑格式檢查',
        passed: true,
        message: '路徑格式正確',
        details: { path: filePath, length: filePath.length }
      };
    } catch (error: any) {
      return {
        testName: '文件路徑格式檢查',
        passed: false,
        message: `檢查失敗: ${error.message}`,
        details: { error }
      };
    }
  }

  /**
   * 測試 4: 檢查文件大小
   */
  testFileSize(fileSize: number): StorageTestResult {
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (fileSize > maxSize) {
      return {
        testName: '文件大小檢查',
        passed: false,
        message: `文件過大: ${(fileSize / 1024 / 1024).toFixed(2)}MB（限制 10MB）`,
        details: { size: fileSize, maxSize }
      };
    }

    return {
      testName: '文件大小檢查',
      passed: true,
      message: `文件大小正常: ${(fileSize / 1024 / 1024).toFixed(2)}MB`,
      details: { size: fileSize }
    };
  }

  /**
   * 運行所有測試
   */
  async runAllTests(userId: string, filePath: string, fileSize: number): Promise<StorageTestResult[]> {
    const results: StorageTestResult[] = [];

    // 測試 1: 認證
    results.push(await this.testAuth());

    // 測試 2: Storage 規則
    results.push(await this.testStorageRules(userId));

    // 測試 3: 文件路徑格式
    results.push(this.testFilePathFormat(filePath));

    // 測試 4: 文件大小
    results.push(this.testFileSize(fileSize));

    return results;
  }

  /**
   * 打印測試結果
   */
  printResults(results: StorageTestResult[]): void {
    console.log('📊 Firebase Storage 測試結果:');
    console.log('='.repeat(50));
    
    results.forEach((result, index) => {
      const status = result.passed ? '✅' : '❌';
      console.log(`${index + 1}. ${status} ${result.testName}`);
      console.log(`   訊息: ${result.message}`);
      if (result.details) {
        console.log(`   詳情:`, result.details);
      }
    });
    
    console.log('='.repeat(50));
    const passedCount = results.filter(r => r.passed).length;
    const totalCount = results.length;
    console.log(`總計: ${passedCount}/${totalCount} 測試通過`);
  }
}

export const firebaseStorageTester = new FirebaseStorageTester();

